import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Calendar as CalendarIcon, Upload, CheckCircle2, ArrowLeft, Phone, Mail } from 'lucide-react';
import { Logo } from '../components/LogoOptions';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card } from '../components/ui/card';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { format } from 'date-fns';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const IntakeForm = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const serviceType = location.state?.serviceType || 'Not Selected';

  // Redirect if no service type selected
  React.useEffect(() => {
    if (serviceType === 'Not Selected') {
      navigate('/', { replace: true });
    }
  }, [serviceType, navigate]);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [fileName, setFileName] = useState('');
  const [agentRole, setAgentRole] = useState(''); // 'listing' or 'buyer'
  const [paymentOption, setPaymentOption] = useState(''); // 'pay_now' or 'pay_at_close'
  const [isWAProperty, setIsWAProperty] = useState(null); // null, 'yes', or 'no'

  const [formData, setFormData] = useState({
    serviceType: serviceType,
    agentRole: '',
    // Agent Information
    listingAgentName: '',
    listingAgentEmail: '',
    listingAgentPhone: '',
    buyerAgentName: '',
    buyerAgentEmail: '',
    buyerAgentPhone: '',
    // Client Information
    sellerName1: '',
    sellerEmail1: '',
    sellerPhone1: '',
    sellerName2: '',
    sellerEmail2: '',
    sellerPhone2: '',
    buyerName1: '',
    buyerEmail1: '',
    buyerPhone1: '',
    buyerName2: '',
    buyerEmail2: '',
    buyerPhone2: '',
    // Property Information
    propertyAddress: '',
    propertyCity: '',
    propertyState: '',
    propertyZip: '',
    // Transaction Dates
    mutualAcceptanceDate: null,
    // Title and Escrow
    titleCompanyName: '',
    escrowOfficerName: '',
    escrowOfficerEmail: '',
    escrowOfficerPhone: '',
    // Additional
    additionalNotes: '',
    uploadedFile: null
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleDateChange = (name, date) => {
    setFormData(prev => ({ ...prev, [name]: date }));
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFileName(file.name);
      setFormData(prev => ({ ...prev, uploadedFile: file }));
    }
  };

  const uploadFile = async (file) => {
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      
      const response = await axios.post(`${API}/upload`, formDataUpload, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (response.data.success) {
        return {
          fileId: response.data.file_id,
          fileName: response.data.file_name,
          fileUrl: response.data.file_url
        };
      } else {
        toast.error(response.data.message || 'File upload failed');
        return null;
      }
    } catch (error) {
      console.error('File upload error:', error);
      toast.error('Failed to upload file');
      return null;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('Form submitted - handleSubmit called');
    setIsSubmitting(true);

    // Basic validation based on service type
    if (!isWAProperty || isWAProperty !== 'yes') {
      console.log('Validation failed: Property must be in Washington State');
      toast.error('We currently only service properties in Washington State');
      setIsSubmitting(false);
      return;
    }

    if (serviceType === 'Listing / Buying Side (In Contract)') {
      if (!agentRole) {
        console.log('Validation failed: agentRole required');
        toast.error('Please select your agent role');
        setIsSubmitting(false);
        return;
      }
      if (!paymentOption) {
        console.log('Validation failed: paymentOption required');
        toast.error('Please select a payment option');
        setIsSubmitting(false);
        return;
      }
      if (!formData.listingAgentName || !formData.listingAgentEmail || !formData.buyerAgentName || !formData.buyerAgentEmail || !formData.propertyAddress) {
        console.log('Validation failed: required fields missing');
        toast.error('Please fill in all required fields');
        setIsSubmitting(false);
        return;
      }
    } else {
      // Listing Side Prep requires payment now
      if (!formData.listingAgentName || !formData.listingAgentEmail || !formData.propertyAddress) {
        console.log('Validation failed: required fields missing');
        toast.error('Please fill in all required fields');
        setIsSubmitting(false);
        return;
      }
    }

    console.log('Validation passed, preparing submission');

    try {
      // Upload file first if one was selected
      let uploadedFileData = null;
      if (formData.uploadedFile && formData.uploadedFile instanceof File) {
        toast.info('Uploading file...');
        uploadedFileData = await uploadFile(formData.uploadedFile);
        if (!uploadedFileData) {
          setIsSubmitting(false);
          return;
        }
        toast.success('File uploaded successfully');
      }

      // Prepare data for submission (convert dates to strings)
      const submissionData = {
        ...formData,
        agentRole: agentRole,
        paymentOption: serviceType === 'Listing Side Prep' ? 'pay_now' : paymentOption,
        mutualAcceptanceDate: formData.mutualAcceptanceDate ? format(formData.mutualAcceptanceDate, 'yyyy-MM-dd') : null,
        uploadedFile: uploadedFileData?.fileName || fileName || null,
        uploadedFileId: uploadedFileData?.fileId || null,
        uploadedFileUrl: uploadedFileData?.fileUrl || null
      };

      // Remove the File object from submission data
      delete submissionData.uploadedFile;
      if (uploadedFileData) {
        submissionData.uploadedFile = uploadedFileData.fileName;
      }

      console.log('Submitting to API:', API, submissionData);

      // Submit to backend API
      const response = await axios.post(`${API}/intake-form`, submissionData);
      
      console.log('API response:', response.data);

      if (response.data.success) {
        toast.success('Form submitted successfully!');
        
        // Check if payment is required
        if (response.data.requires_payment) {
          console.log('Payment required, redirecting to payment page');
          // Determine package ID
          let packageId;
          if (serviceType === 'Listing Side Prep') {
            packageId = 'listing_prep';
          } else if (paymentOption === 'pay_now') {
            packageId = 'listing_buying_pay_now';
          }
          
          // Navigate to payment page
          navigate('/payment', {
            state: {
              submissionId: response.data.submission_id,
              packageId: packageId,
              amount: packageId === 'listing_buying_pay_now' ? 270 : 300,
              serviceType: serviceType
            }
          });
        } else {
          console.log('No payment required, showing success');
          // Pay at close - show success
          setShowSuccess(true);
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      } else {
        console.error('API returned success=false:', response.data);
        toast.error(response.data.message || 'Failed to submit form. Please try again.');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      console.error('Error details:', error.response?.data);
      toast.error('Failed to submit form. Please try again or call us at 206-627-0768.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const DatePicker = ({ value, onChange, placeholder }) => {
    return (
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className="w-full justify-start text-left font-normal h-11"
          >
            <CalendarIcon className="mr-2 h-4 w-4 text-gray-500" />
            {value ? format(value, 'PPP') : <span className="text-gray-500">{placeholder}</span>}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            selected={value}
            onSelect={onChange}
            initialFocus
          />
        </PopoverContent>
      </Popover>
    );
  };

  if (showSuccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
        <Card className="max-w-2xl w-full p-12 text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-12 h-12 text-green-600" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            Transaction Details Received!
          </h1>
          <p className="text-lg text-gray-600 mb-8">
            Thank you for submitting your transaction details. We've received your information and will contact you shortly to begin coordination.
          </p>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 mb-8">
            <p className="font-semibold text-gray-900 mb-2">What happens next?</p>
            <p className="text-gray-600">
              Our team will review your submission and reach out within 24 hours to confirm details and kick off your transaction coordination.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={() => navigate('/')}
              className="bg-gray-900 hover:bg-gray-800 text-white px-8 py-6 text-lg"
            >
              Return to Home
            </Button>
            <Button 
              variant="outline"
              onClick={() => {
                setShowSuccess(false);
                setAgentRole('');
                setFormData({
                  serviceType: serviceType,
                  agentRole: '',
                  listingAgentName: '',
                  listingAgentEmail: '',
                  listingAgentPhone: '',
                  buyerAgentName: '',
                  buyerAgentEmail: '',
                  buyerAgentPhone: '',
                  sellerName1: '',
                  sellerEmail1: '',
                  sellerPhone1: '',
                  sellerName2: '',
                  sellerEmail2: '',
                  sellerPhone2: '',
                  buyerName1: '',
                  buyerEmail1: '',
                  buyerPhone1: '',
                  buyerName2: '',
                  buyerEmail2: '',
                  buyerPhone2: '',
                  propertyAddress: '',
                  propertyCity: '',
                  propertyState: '',
                  propertyZip: '',
                  mutualAcceptanceDate: null,
                  titleCompanyName: '',
                  escrowOfficerName: '',
                  escrowOfficerEmail: '',
                  escrowOfficerPhone: '',
                  additionalNotes: '',
                  uploadedFile: null
                });
                setFileName('');
              }}
              className="px-8 py-6 text-lg"
            >
              Submit Another Transaction
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Logo />
          <div className="flex items-center gap-6 text-sm">
            {/* Mobile: Icon only */}
            <a href="tel:206-627-0768" className="sm:hidden flex items-center justify-center w-10 h-10 bg-amber-500 hover:bg-amber-600 text-white rounded-full transition-colors shadow-md">
              <Phone className="w-5 h-5" />
            </a>
            {/* Desktop: Full contact info */}
            <a href="tel:206-627-0768" className="hidden sm:flex items-center gap-2 text-gray-700 hover:text-amber-600 transition-colors">
              <Phone className="w-4 h-4" />
              <span className="font-medium">206-627-0768</span>
            </a>
            <a href="mailto:info@ezflowtc.com" className="hidden md:flex items-center gap-2 text-gray-700 hover:text-amber-600 transition-colors">
              <Mail className="w-4 h-4" />
              <span className="font-medium">info@ezflowtc.com</span>
            </a>
          </div>
        </div>
      </header>

      {/* Form Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className="mb-6 text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Services
          </Button>

          {/* Service Type Banner */}
          <div className="bg-amber-500 text-white rounded-xl p-6 mb-8 shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold opacity-90 mb-1">Selected Service</p>
                <h2 className="text-2xl font-bold">{serviceType}</h2>
              </div>
              <div className="text-right">
                <p className="text-4xl font-bold">$300</p>
                <p className="text-sm opacity-90">Flat Rate</p>
              </div>
            </div>
          </div>

          {/* Service Area Disclaimer */}
          <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-r-lg mb-8">
            <p className="text-sm text-blue-900">
              <span className="font-semibold">Service Area:</span> We proudly serve transactions within Washington State and the Northwest Multiple Listing Service (NWMLS).
            </p>
          </div>

          <Card className="p-8 lg:p-12 shadow-xl">
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Transaction Intake Form</h1>
              <p className="text-gray-600">Please fill out all required information to begin coordination</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-10">
              {/* Washington State Eligibility Question - Required First */}
              <section>
                <h3 className="text-xl font-bold text-gray-900 mb-6 pb-3 border-b-2 border-amber-500">
                  Service Area Eligibility
                </h3>
                <div className="bg-gradient-to-br from-blue-50 to-white border-2 border-blue-200 rounded-lg p-6">
                  <Label className="text-gray-700 font-semibold mb-4 block">
                    Is the transaction's subject property located in Washington State? <span className="text-red-500">*</span>
                  </Label>
                  <p className="text-sm text-gray-600 mb-4">
                    We are currently only offering this service for properties located within WA.
                  </p>
                  <RadioGroup value={isWAProperty || ''} onValueChange={setIsWAProperty} className="space-y-3">
                    <label 
                      htmlFor="wa_yes"
                      className={`flex items-center space-x-4 p-4 rounded-lg cursor-pointer transition-all duration-300 ${
                        isWAProperty === 'yes' 
                          ? 'bg-green-500 border-2 border-green-600 shadow-lg' 
                          : 'bg-white border-2 border-gray-200 hover:border-green-400 hover:shadow-md'
                      }`}
                    >
                      <RadioGroupItem value="yes" id="wa_yes" className="hidden" />
                      <div className={`font-semibold ${isWAProperty === 'yes' ? 'text-white' : 'text-gray-900'}`}>
                        Yes, the property is in Washington State
                      </div>
                      {isWAProperty === 'yes' && (
                        <CheckCircle2 className="w-6 h-6 text-white ml-auto" />
                      )}
                    </label>
                    <label 
                      htmlFor="wa_no"
                      className={`flex items-center space-x-4 p-4 rounded-lg cursor-pointer transition-all duration-300 ${
                        isWAProperty === 'no' 
                          ? 'bg-red-500 border-2 border-red-600 shadow-lg' 
                          : 'bg-white border-2 border-gray-200 hover:border-red-400 hover:shadow-md'
                      }`}
                    >
                      <RadioGroupItem value="no" id="wa_no" className="hidden" />
                      <div className={`font-semibold ${isWAProperty === 'no' ? 'text-white' : 'text-gray-900'}`}>
                        No, the property is outside Washington State
                      </div>
                      {isWAProperty === 'no' && (
                        <CheckCircle2 className="w-6 h-6 text-white ml-auto" />
                      )}
                    </label>
                  </RadioGroup>
                  
                  {/* Warning message if No is selected */}
                  {isWAProperty === 'no' && (
                    <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-red-800 font-semibold">
                        We apologize, but we are currently only offering this service for transactions within Washington State.
                      </p>
                      <p className="text-red-700 text-sm mt-2">
                        Please check back in the future as we expand our service areas. For questions, contact us at{' '}
                        <a href="tel:206-627-0768" className="underline font-medium">206-627-0768</a> or{' '}
                        <a href="mailto:info@ezflowtc.com" className="underline font-medium">info@ezflowtc.com</a>.
                      </p>
                    </div>
                  )}
                </div>
              </section>

              {/* Only show rest of form if WA property is confirmed */}
              {isWAProperty === 'yes' && (
                <>
              {/* Agent Role Selection - Only for Listing / Buying Side */}
              {serviceType === 'Listing / Buying Side (In Contract)' && (
                <>
                  <section>
                    <h3 className="text-xl font-bold text-gray-900 mb-6 pb-3 border-b-2 border-amber-500">
                      Your Role
                    </h3>
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-6">
                      <Label className="text-gray-700 font-semibold mb-4 block">
                        Are you the Listing Agent or Buyer's Agent? <span className="text-red-500">*</span>
                      </Label>
                      <RadioGroup value={agentRole} onValueChange={setAgentRole} className="space-y-3">
                        <div className="flex items-center space-x-3 bg-white p-4 rounded-lg border-2 border-gray-200 hover:border-amber-500 transition-colors cursor-pointer">
                          <RadioGroupItem value="listing" id="listing" />
                          <Label htmlFor="listing" className="cursor-pointer flex-1 font-medium">
                            I am the Listing Agent
                          </Label>
                        </div>
                        <div className="flex items-center space-x-3 bg-white p-4 rounded-lg border-2 border-gray-200 hover:border-amber-500 transition-colors cursor-pointer">
                          <RadioGroupItem value="buyer" id="buyer" />
                          <Label htmlFor="buyer" className="cursor-pointer flex-1 font-medium">
                            I am the Buyer's Agent
                          </Label>
                        </div>
                      </RadioGroup>
                    </div>
                  </section>

                  {/* Payment Option Selection */}
                  <section>
                    <h3 className="text-xl font-bold text-gray-900 mb-6 pb-3 border-b-2 border-amber-500">
                      Payment Option
                    </h3>
                    <div className="bg-gradient-to-br from-amber-50 to-white border-2 border-amber-200 rounded-lg p-6">
                      <Label className="text-gray-700 font-semibold mb-4 block">
                        Choose your payment preference <span className="text-red-500">*</span>
                      </Label>
                      <RadioGroup value={paymentOption} onValueChange={setPaymentOption} className="space-y-4">
                        <label 
                          htmlFor="pay_now"
                          className={`flex items-start space-x-4 p-6 rounded-lg cursor-pointer transition-all duration-300 ${
                            paymentOption === 'pay_now' 
                              ? 'bg-amber-500 border-4 border-amber-600 shadow-xl' 
                              : 'bg-white border-2 border-gray-300 hover:border-amber-400 hover:shadow-lg'
                          }`}
                        >
                          <RadioGroupItem value="pay_now" id="pay_now" className="mt-1 hidden" />
                          <div className="flex-1">
                            <div className={`font-bold text-xl mb-2 flex items-center gap-2 ${
                              paymentOption === 'pay_now' ? 'text-white' : 'text-gray-900'
                            }`}>
                              Pay Now 
                              <span className={`text-xs px-3 py-1 rounded-full font-bold ${
                                paymentOption === 'pay_now' 
                                  ? 'bg-white text-green-600' 
                                  : 'bg-green-500 text-white'
                              }`}>
                                SAVE 10%!
                              </span>
                            </div>
                            <p className={`text-lg mb-2 ${
                              paymentOption === 'pay_now' ? 'text-white' : 'text-gray-700'
                            }`}>
                              Pay only <span className="font-bold text-2xl">${paymentOption === 'pay_now' ? '270' : '270'}</span> 
                              <span className={`ml-2 line-through ${
                                paymentOption === 'pay_now' ? 'text-amber-100' : 'text-gray-400'
                              }`}>
                                $300
                              </span>
                            </p>
                            <p className={`text-sm ${
                              paymentOption === 'pay_now' ? 'text-amber-50' : 'text-gray-600'
                            }`}>
                              Save $30 by paying upfront via Stripe, Venmo, or Zelle
                            </p>
                          </div>
                          {paymentOption === 'pay_now' && (
                            <CheckCircle2 className="w-8 h-8 text-white flex-shrink-0" />
                          )}
                        </label>

                        <label 
                          htmlFor="pay_at_close"
                          className={`flex items-start space-x-4 p-6 rounded-lg cursor-pointer transition-all duration-300 ${
                            paymentOption === 'pay_at_close' 
                              ? 'bg-gray-700 border-4 border-gray-900 shadow-xl' 
                              : 'bg-white border-2 border-gray-300 hover:border-gray-500 hover:shadow-lg'
                          }`}
                        >
                          <RadioGroupItem value="pay_at_close" id="pay_at_close" className="mt-1 hidden" />
                          <div className="flex-1">
                            <div className={`font-bold text-xl mb-2 ${
                              paymentOption === 'pay_at_close' ? 'text-white' : 'text-gray-900'
                            }`}>
                              Pay at Close
                            </div>
                            <p className={`text-lg mb-2 ${
                              paymentOption === 'pay_at_close' ? 'text-white' : 'text-gray-700'
                            }`}>
                              Pay <span className="font-bold text-2xl">$300</span> at closing
                            </p>
                            <p className={`text-sm ${
                              paymentOption === 'pay_at_close' ? 'text-gray-300' : 'text-gray-600'
                            }`}>
                              We'll send an invoice payable at transaction close
                            </p>
                          </div>
                          {paymentOption === 'pay_at_close' && (
                            <CheckCircle2 className="w-8 h-8 text-white flex-shrink-0" />
                          )}
                        </label>
                      </RadioGroup>
                    </div>
                  </section>
                </>
              )}

              {/* Payment Info for Listing Side Prep */}
              {serviceType === 'Listing Side Prep' && (
                <section>
                  <h3 className="text-xl font-bold text-gray-900 mb-6 pb-3 border-b-2 border-amber-500">
                    Payment Required
                  </h3>
                  <div className="bg-gradient-to-br from-blue-50 to-white border-2 border-blue-200 rounded-lg p-6">
                    <div className="flex items-start gap-4">
                      <CheckCircle2 className="w-8 h-8 text-blue-600 flex-shrink-0 mt-1" />
                      <div>
                        <h4 className="font-bold text-lg text-gray-900 mb-2">Payment Due Now</h4>
                        <p className="text-gray-700 mb-3">
                          Listing Side Prep requires upfront payment of <span className="font-bold text-blue-600 text-xl">$300</span>
                        </p>
                        <p className="text-sm text-gray-600">
                          After submitting this form, you'll be directed to our secure payment page where you can pay via Stripe, Venmo, or Zelle.
                        </p>
                      </div>
                    </div>
                  </div>
                </section>
              )}

              {/* Agent Information */}
              <section>
                <h3 className="text-xl font-bold text-gray-900 mb-6 pb-3 border-b-2 border-amber-500">
                  Agent Information
                </h3>
                <div className="space-y-6">
                  <div>
                    <p className="text-sm font-semibold text-gray-700 mb-4">Listing Agent {serviceType === 'Listing / Buying Side (In Contract)' ? <span className="text-red-500">*</span> : ''}</p>
                    <div className="grid md:grid-cols-3 gap-6">
                      <div>
                        <Label htmlFor="listingAgentName" className="text-gray-700 font-semibold mb-2 block">
                          Name <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="listingAgentName"
                          name="listingAgentName"
                          value={formData.listingAgentName}
                          onChange={handleInputChange}
                          placeholder="John Smith"
                          required
                          className="h-11"
                        />
                      </div>
                      <div>
                        <Label htmlFor="listingAgentEmail" className="text-gray-700 font-semibold mb-2 block">
                          Email <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="listingAgentEmail"
                          name="listingAgentEmail"
                          type="email"
                          value={formData.listingAgentEmail}
                          onChange={handleInputChange}
                          placeholder="john@agency.com"
                          required
                          className="h-11"
                        />
                      </div>
                      <div>
                        <Label htmlFor="listingAgentPhone" className="text-gray-700 font-semibold mb-2 block">
                          Phone <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="listingAgentPhone"
                          name="listingAgentPhone"
                          type="tel"
                          value={formData.listingAgentPhone}
                          onChange={handleInputChange}
                          placeholder="(555) 123-4567"
                          required
                          className="h-11"
                        />
                      </div>
                    </div>
                  </div>

                  {serviceType === 'Listing / Buying Side (In Contract)' && (
                    <div className="pt-4">
                      <p className="text-sm font-semibold text-gray-700 mb-4">Buyer's Agent <span className="text-red-500">*</span></p>
                      <div className="grid md:grid-cols-3 gap-6">
                        <div>
                          <Label htmlFor="buyerAgentName" className="text-gray-700 font-semibold mb-2 block">
                            Name <span className="text-red-500">*</span>
                          </Label>
                          <Input
                            id="buyerAgentName"
                            name="buyerAgentName"
                            value={formData.buyerAgentName}
                            onChange={handleInputChange}
                            placeholder="Jane Doe"
                            required
                            className="h-11"
                          />
                        </div>
                        <div>
                          <Label htmlFor="buyerAgentEmail" className="text-gray-700 font-semibold mb-2 block">
                            Email <span className="text-red-500">*</span>
                          </Label>
                          <Input
                            id="buyerAgentEmail"
                            name="buyerAgentEmail"
                            type="email"
                            value={formData.buyerAgentEmail}
                            onChange={handleInputChange}
                            placeholder="jane@agency.com"
                            required
                            className="h-11"
                          />
                        </div>
                        <div>
                          <Label htmlFor="buyerAgentPhone" className="text-gray-700 font-semibold mb-2 block">
                            Phone <span className="text-red-500">*</span>
                          </Label>
                          <Input
                            id="buyerAgentPhone"
                            name="buyerAgentPhone"
                            type="tel"
                            value={formData.buyerAgentPhone}
                            onChange={handleInputChange}
                            placeholder="(555) 987-6543"
                            required
                            className="h-11"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </section>

              {/* Client Information */}
              <section>
                <h3 className="text-xl font-bold text-gray-900 mb-6 pb-3 border-b-2 border-amber-500">
                  Client Information
                </h3>
                <div className="space-y-6">
                  <div>
                    <p className="text-sm font-semibold text-gray-700 mb-4">Seller(s) Information</p>
                    <div className="grid md:grid-cols-3 gap-6 mb-4">
                      <div>
                        <Label htmlFor="sellerName1" className="text-gray-700 font-medium mb-2 block">
                          Seller Name
                        </Label>
                        <Input
                          id="sellerName1"
                          name="sellerName1"
                          value={formData.sellerName1}
                          onChange={handleInputChange}
                          placeholder="First seller"
                          className="h-11"
                        />
                      </div>
                      <div>
                        <Label htmlFor="sellerEmail1" className="text-gray-700 font-medium mb-2 block">
                          Email
                        </Label>
                        <Input
                          id="sellerEmail1"
                          name="sellerEmail1"
                          type="email"
                          value={formData.sellerEmail1}
                          onChange={handleInputChange}
                          placeholder="seller@email.com"
                          className="h-11"
                        />
                      </div>
                      <div>
                        <Label htmlFor="sellerPhone1" className="text-gray-700 font-medium mb-2 block">
                          Phone
                        </Label>
                        <Input
                          id="sellerPhone1"
                          name="sellerPhone1"
                          type="tel"
                          value={formData.sellerPhone1}
                          onChange={handleInputChange}
                          placeholder="(555) 111-2222"
                          className="h-11"
                        />
                      </div>
                    </div>
                    <div className="grid md:grid-cols-3 gap-6">
                      <div>
                        <Label htmlFor="sellerName2" className="text-gray-700 font-medium mb-2 block">
                          Additional Seller (Optional)
                        </Label>
                        <Input
                          id="sellerName2"
                          name="sellerName2"
                          value={formData.sellerName2}
                          onChange={handleInputChange}
                          placeholder="Second seller"
                          className="h-11"
                        />
                      </div>
                      <div>
                        <Label htmlFor="sellerEmail2" className="text-gray-700 font-medium mb-2 block">
                          Email
                        </Label>
                        <Input
                          id="sellerEmail2"
                          name="sellerEmail2"
                          type="email"
                          value={formData.sellerEmail2}
                          onChange={handleInputChange}
                          placeholder="seller2@email.com"
                          className="h-11"
                        />
                      </div>
                      <div>
                        <Label htmlFor="sellerPhone2" className="text-gray-700 font-medium mb-2 block">
                          Phone
                        </Label>
                        <Input
                          id="sellerPhone2"
                          name="sellerPhone2"
                          type="tel"
                          value={formData.sellerPhone2}
                          onChange={handleInputChange}
                          placeholder="(555) 333-4444"
                          className="h-11"
                        />
                      </div>
                    </div>
                  </div>

                  {serviceType === 'Listing / Buying Side (In Contract)' && (
                    <div className="pt-4">
                      <p className="text-sm font-semibold text-gray-700 mb-4">Buyer(s) Information</p>
                    <div className="grid md:grid-cols-3 gap-6 mb-4">
                      <div>
                        <Label htmlFor="buyerName1" className="text-gray-700 font-medium mb-2 block">
                          Buyer Name
                        </Label>
                        <Input
                          id="buyerName1"
                          name="buyerName1"
                          value={formData.buyerName1}
                          onChange={handleInputChange}
                          placeholder="First buyer"
                          className="h-11"
                        />
                      </div>
                      <div>
                        <Label htmlFor="buyerEmail1" className="text-gray-700 font-medium mb-2 block">
                          Email
                        </Label>
                        <Input
                          id="buyerEmail1"
                          name="buyerEmail1"
                          type="email"
                          value={formData.buyerEmail1}
                          onChange={handleInputChange}
                          placeholder="buyer@email.com"
                          className="h-11"
                        />
                      </div>
                      <div>
                        <Label htmlFor="buyerPhone1" className="text-gray-700 font-medium mb-2 block">
                          Phone
                        </Label>
                        <Input
                          id="buyerPhone1"
                          name="buyerPhone1"
                          type="tel"
                          value={formData.buyerPhone1}
                          onChange={handleInputChange}
                          placeholder="(555) 555-6666"
                          className="h-11"
                        />
                      </div>
                    </div>
                    <div className="grid md:grid-cols-3 gap-6">
                      <div>
                        <Label htmlFor="buyerName2" className="text-gray-700 font-medium mb-2 block">
                          Additional Buyer (Optional)
                        </Label>
                        <Input
                          id="buyerName2"
                          name="buyerName2"
                          value={formData.buyerName2}
                          onChange={handleInputChange}
                          placeholder="Second buyer"
                          className="h-11"
                        />
                      </div>
                      <div>
                        <Label htmlFor="buyerEmail2" className="text-gray-700 font-medium mb-2 block">
                          Email
                        </Label>
                        <Input
                          id="buyerEmail2"
                          name="buyerEmail2"
                          type="email"
                          value={formData.buyerEmail2}
                          onChange={handleInputChange}
                          placeholder="buyer2@email.com"
                          className="h-11"
                        />
                      </div>
                      <div>
                        <Label htmlFor="buyerPhone2" className="text-gray-700 font-medium mb-2 block">
                          Phone
                        </Label>
                        <Input
                          id="buyerPhone2"
                          name="buyerPhone2"
                          type="tel"
                          value={formData.buyerPhone2}
                          onChange={handleInputChange}
                          placeholder="(555) 777-8888"
                          className="h-11"
                        />
                      </div>
                    </div>
                    </div>
                  )}
                </div>
              </section>

              {/* Property Information */}
              <section>
                <h3 className="text-xl font-bold text-gray-900 mb-6 pb-3 border-b-2 border-amber-500">
                  Property Information
                </h3>
                <div className="space-y-6">
                  <div>
                    <Label htmlFor="propertyAddress" className="text-gray-700 font-semibold mb-2 block">
                      Property Address <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="propertyAddress"
                      name="propertyAddress"
                      value={formData.propertyAddress}
                      onChange={handleInputChange}
                      placeholder="123 Main Street"
                      required
                      className="h-11"
                    />
                  </div>
                  <div className="grid md:grid-cols-3 gap-6">
                    <div>
                      <Label htmlFor="propertyCity" className="text-gray-700 font-semibold mb-2 block">
                        City <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="propertyCity"
                        name="propertyCity"
                        value={formData.propertyCity}
                        onChange={handleInputChange}
                        placeholder="Seattle"
                        required
                        className="h-11"
                      />
                    </div>
                    <div>
                      <Label htmlFor="propertyState" className="text-gray-700 font-semibold mb-2 block">
                        State <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="propertyState"
                        name="propertyState"
                        value={formData.propertyState}
                        onChange={handleInputChange}
                        placeholder="WA"
                        required
                        className="h-11"
                      />
                    </div>
                    <div>
                      <Label htmlFor="propertyZip" className="text-gray-700 font-semibold mb-2 block">
                        ZIP Code <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="propertyZip"
                        name="propertyZip"
                        value={formData.propertyZip}
                        onChange={handleInputChange}
                        placeholder="98101"
                        required
                        className="h-11"
                      />
                    </div>
                  </div>
                </div>
              </section>

              {/* Transaction Dates - Only for Listing / Buying Side */}
              {serviceType === 'Listing / Buying Side (In Contract)' && (
                <section>
                  <h3 className="text-xl font-bold text-gray-900 mb-6 pb-3 border-b-2 border-amber-500">
                    Transaction Date
                  </h3>
                  <div className="max-w-md">
                    <Label className="text-gray-700 font-semibold mb-2 block">
                      Mutual Acceptance Date
                    </Label>
                    <DatePicker
                      value={formData.mutualAcceptanceDate}
                      onChange={(date) => handleDateChange('mutualAcceptanceDate', date)}
                      placeholder="Select date"
                    />
                  </div>
                </section>
              )}

              {/* Title and Escrow */}
              <section>
                <h3 className="text-xl font-bold text-gray-900 mb-6 pb-3 border-b-2 border-amber-500">
                  Title & Escrow Details
                </h3>
                <div className="space-y-6">
                  <div>
                    <Label htmlFor="titleCompanyName" className="text-gray-700 font-semibold mb-2 block">
                      Title Company Name
                    </Label>
                    <Input
                      id="titleCompanyName"
                      name="titleCompanyName"
                      value={formData.titleCompanyName}
                      onChange={handleInputChange}
                      placeholder="ABC Title Company"
                      className="h-11"
                    />
                  </div>
                  <div className="grid md:grid-cols-3 gap-6">
                    <div>
                      <Label htmlFor="escrowOfficerName" className="text-gray-700 font-semibold mb-2 block">
                        Escrow Officer Name
                      </Label>
                      <Input
                        id="escrowOfficerName"
                        name="escrowOfficerName"
                        value={formData.escrowOfficerName}
                        onChange={handleInputChange}
                        placeholder="Sarah Johnson"
                        className="h-11"
                      />
                    </div>
                    <div>
                      <Label htmlFor="escrowOfficerEmail" className="text-gray-700 font-semibold mb-2 block">
                        Email
                      </Label>
                      <Input
                        id="escrowOfficerEmail"
                        name="escrowOfficerEmail"
                        type="email"
                        value={formData.escrowOfficerEmail}
                        onChange={handleInputChange}
                        placeholder="sarah@titleco.com"
                        className="h-11"
                      />
                    </div>
                    <div>
                      <Label htmlFor="escrowOfficerPhone" className="text-gray-700 font-semibold mb-2 block">
                        Phone
                      </Label>
                      <Input
                        id="escrowOfficerPhone"
                        name="escrowOfficerPhone"
                        type="tel"
                        value={formData.escrowOfficerPhone}
                        onChange={handleInputChange}
                        placeholder="(555) 999-0000"
                        className="h-11"
                      />
                    </div>
                  </div>
                </div>
              </section>

              {/* File Upload & Notes */}
              <section>
                <h3 className="text-xl font-bold text-gray-900 mb-6 pb-3 border-b-2 border-amber-500">
                  Additional Information
                </h3>
                <div className="space-y-6">
                  <div>
                    <Label htmlFor="fileUpload" className="text-gray-700 font-semibold mb-2 block">
                      Upload Contracts or Addenda (Optional)
                    </Label>
                    <div className="relative">
                      <Input
                        id="fileUpload"
                        type="file"
                        onChange={handleFileUpload}
                        className="hidden"
                        accept=".pdf,.doc,.docx"
                      />
                      <label
                        htmlFor="fileUpload"
                        className="flex items-center justify-center gap-2 w-full h-24 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-amber-500 hover:bg-amber-50 transition-colors"
                      >
                        <Upload className="w-6 h-6 text-gray-400" />
                        <span className="text-gray-600">
                          {fileName || 'Click to upload or drag and drop'}
                        </span>
                      </label>
                    </div>
                    {fileName && (
                      <p className="text-sm text-green-600 mt-2 flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4" />
                        File uploaded: {fileName}
                      </p>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="additionalNotes" className="text-gray-700 font-semibold mb-2 block">
                      Additional Notes or Special Instructions
                    </Label>
                    <Textarea
                      id="additionalNotes"
                      name="additionalNotes"
                      value={formData.additionalNotes}
                      onChange={handleInputChange}
                      placeholder="Please include any special circumstances, rush requests, or important details we should know..."
                      rows={6}
                      className="resize-none"
                    />
                  </div>
                </div>
              </section>

              {/* Submit Button */}
              <div className="pt-6">
                <Button
                  type="submit"
                  disabled={isSubmitting || isWAProperty !== 'yes'}
                  className={`w-full py-6 text-lg font-bold shadow-lg transition-all duration-300 ${
                    isWAProperty === 'yes' 
                      ? 'bg-amber-600 hover:bg-amber-700 text-white hover:shadow-xl' 
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Transaction Details'}
                </Button>
                <p className="text-center text-sm text-gray-500 mt-4">
                  By submitting, you agree to receive coordination services from EZ Flow TC
                </p>
              </div>
              </>
              )}
            </form>
          </Card>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 mt-20">
        <div className="container mx-auto px-4 text-center">
          <p className="text-sm text-gray-400">
            © {new Date().getFullYear()} EZ Flow TC. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default IntakeForm;
